import { defineMessages } from 'react-intl';

/**
 * Search panel spec (search-panel-spec.md) step 3: every user-visible chrome string in
 * SearchPanel.tsx (headings, buttons, hints, aria labels). Facet VALUES themselves (city names,
 * skill names, role titles, ...) are data, already localized server-side per collection, and are
 * never routed through this file. Wording for concepts that already exist elsewhere in the app
 * (Remote/Hybrid/Onsite, "Clear search") matches those existing strings
 * (marketHeader.workModelSeo.*, bestCompanies.hub.clearSearch) rather than inventing new phrasing,
 * per CLAUDE.md's i18n rule.
 */
export const searchPanelMessages = defineMessages({
  inputAriaLabel: {
    id: 'searchPanel.inputAriaLabel',
    defaultMessage: 'Search jobs',
    description: 'Visually-hidden label for the search panel text input',
  },
  clearAria: {
    id: 'searchPanel.clearAria',
    defaultMessage: 'Clear search',
    description: 'Aria label for the button that clears the search panel input',
  },
  goAria: {
    id: 'searchPanel.goAria',
    defaultMessage: 'Search',
    description: 'Aria label for the arrow button in the search panel input row that runs the typed search',
  },
  closeAria: {
    id: 'searchPanel.closeAria',
    defaultMessage: 'Close search',
    description: 'Aria label for the fullscreen search view back arrow',
  },
  recentHeading: {
    id: 'searchPanel.recentHeading',
    defaultMessage: 'Recent',
    description: 'Search panel launchpad section heading: recent searches',
  },
  launchpadUnavailable: {
    id: 'searchPanel.launchpadUnavailable',
    defaultMessage: "Couldn't load suggestions right now. You can still search directly.",
    description: 'Search panel launchpad empty state shown when the data fetch fails (offline, backend down, etc.)',
  },
  browseFieldHeading: {
    id: 'searchPanel.browseFieldHeading',
    defaultMessage: 'Browse a field',
    description: 'Search panel launchpad section heading: top fields',
  },
  workSetupHeading: {
    id: 'searchPanel.workSetupHeading',
    defaultMessage: 'Work setup',
    description: 'Search panel launchpad section heading: remote/hybrid/onsite split',
  },
  workSetupRemote: {
    id: 'searchPanel.workSetupRemote',
    defaultMessage: 'Remote',
    description: 'Work-setup chart legend: remote jobs (matches marketHeader.workModelSeo.remote)',
  },
  workSetupHybrid: {
    id: 'searchPanel.workSetupHybrid',
    defaultMessage: 'Hybrid',
    description: 'Work-setup chart legend: hybrid jobs (matches marketHeader.workModelSeo.hybrid)',
  },
  workSetupOnsite: {
    id: 'searchPanel.workSetupOnsite',
    defaultMessage: 'Onsite',
    description: 'Work-setup chart legend: onsite jobs (matches marketHeader.workModelSeo.onsite)',
  },
  rightNowKicker: {
    id: 'searchPanel.rightNowKicker',
    defaultMessage: 'Right now',
    description: 'Search panel launchpad readout kicker label above the open-jobs count',
  },
  baselineFinland: {
    id: 'searchPanel.baselineFinland',
    defaultMessage: 'In Finland',
    description: 'Readout context line: baseline scope label when the SRP\'s Finland/World toggle is set to Finland (the default)',
  },
  baselineWorldwide: {
    id: 'searchPanel.baselineWorldwide',
    defaultMessage: 'Worldwide',
    description: 'Readout context line: baseline scope label when the SRP\'s Finland/World toggle is set to World',
  },
  scopeMoreFilters: {
    id: 'searchPanel.scopeMoreFilters',
    defaultMessage: '+{count}',
    description: 'Readout context line: how many more active filters are not individually shown as a chip',
  },
  openJobsSuffix: {
    id: 'searchPanel.openJobsSuffix',
    defaultMessage: 'open jobs',
    description: 'Search panel launchpad readout: suffix after the open-jobs count',
  },
  whereTheyAreKicker: {
    id: 'searchPanel.whereTheyAreKicker',
    defaultMessage: 'Where they are',
    description: 'Search panel launchpad readout kicker label above the top-locations list',
  },
  addWithItHeading: {
    id: 'searchPanel.addWithItHeading',
    defaultMessage: 'Add with it',
    description: 'Search panel typing-state section heading: co-occurring skills to add',
  },
  topMatchesHeading: {
    id: 'searchPanel.topMatchesHeading',
    defaultMessage: 'Top matches',
    description: 'Search panel section heading: first few matching jobs',
  },
  allCount: {
    id: 'searchPanel.allCount',
    defaultMessage: 'All {count}',
    description: 'Link text: see all N matching jobs (count is already locale-formatted)',
  },
  jobsSuffix: {
    id: 'searchPanel.jobsSuffix',
    defaultMessage: 'jobs',
    description: 'Suffix after a job count in the search panel readout',
  },
  needFinnish: {
    id: 'searchPanel.needFinnish',
    defaultMessage: 'need Finnish',
    description: 'Search panel readout: label under the "requires Finnish" percentage ring',
  },
  showJobsButton: {
    id: 'searchPanel.showJobsButton',
    defaultMessage: 'Show {count} jobs',
    description: 'Parsed-state solid call-to-action button (count is already locale-formatted)',
  },
  searchAsPlainWords: {
    id: 'searchPanel.searchAsPlainWords',
    defaultMessage: 'Search as plain words',
    description: 'Parsed-state link: search the typed text as free text instead of the parsed filters',
  },
  widenItHeading: {
    id: 'searchPanel.widenItHeading',
    defaultMessage: 'Widen it',
    description: 'Parsed-state section heading: cards to drop one word and widen the result set',
  },
  firstMatchesHeading: {
    id: 'searchPanel.firstMatchesHeading',
    defaultMessage: 'First matches',
    description: 'Parsed-state section heading: first few matching jobs',
  },
  narrowsHeading: {
    id: 'searchPanel.narrowsHeading',
    defaultMessage: 'How each word narrows it',
    description: 'Parsed-state readout kicker: step-by-step narrowing funnel',
  },
  insideTheseHeading: {
    id: 'searchPanel.insideTheseHeading',
    defaultMessage: 'Inside these {count}',
    description: 'Parsed-state readout kicker above the remoteness split for the narrowed result set',
  },
  oftenWithHeading: {
    id: 'searchPanel.oftenWithHeading',
    defaultMessage: 'Often with',
    description: 'Parsed-state readout kicker: co-occurring skills within the narrowed result set',
  },
  hintTyping: {
    id: 'searchPanel.hintTyping',
    defaultMessage: 'Up/down to move, Enter to search, Tab to add as a filter',
    description: 'Keyboard-shortcut hint shown at the bottom of the launchpad/typing states',
  },
  hintParsed: {
    id: 'searchPanel.hintParsed',
    defaultMessage: 'Backspace to remove the last word, Enter to show jobs',
    description: 'Keyboard-shortcut hint shown at the bottom of the parsed state',
  },
  exampleHint: {
    id: 'searchPanel.exampleHint',
    defaultMessage: 'Try typing "senior react helsinki"',
    description: 'Clickable example shown at the bottom-left of the launchpad footer (opposite the keyboard-shortcut hint); clicking it fills the input with this exact example query and runs it, the same as if the visitor had typed it themselves.',
  },
  removeTokenAria: {
    id: 'searchPanel.removeTokenAria',
    defaultMessage: 'Remove {label}',
    description: 'Aria label for the remove button on a parsed-state filter chip',
  },
  textRowKind: {
    id: 'searchPanel.textRowKind',
    defaultMessage: 'Text',
    description: 'Badge on the "search anywhere" suggestion row, distinguishing it from facet rows',
  },
  anywhereSuffix: {
    id: 'searchPanel.anywhereSuffix',
    defaultMessage: 'anywhere',
    description: 'Suffix on the text-search row, e.g. "react anywhere"',
  },
  allOpenJobs: {
    id: 'searchPanel.allOpenJobs',
    defaultMessage: 'All open jobs',
    description: 'Parsed-state narrowing funnel: label for step 0, before any word is applied (matches navigation.menu.note.jobs\'s phrasing)',
  },
  remoteWorldwide: {
    id: 'searchPanel.remoteWorldwide',
    // Round 10 (user feedback): "{name}, worldwide" (-> "Remote, worldwide") truncated with an
    // ellipsis in the "Where they are" label column even after it was widened; "Remote (global)"
    // matches remoteScoped's own shorter "Remote (scope)" pattern and fits.
    defaultMessage: '{name} (global)',
    description: 'Label for the "Remote - Global" pseudo-location in the "Where they are" list: short enough to fit the label column without truncating (round 10, was "{name}, worldwide")',
  },
  remoteScoped: {
    id: 'searchPanel.remoteScoped',
    defaultMessage: 'Remote ({scope})',
    description: 'Coordinator review round 7: "Where they are" label for any remote-scoped pseudo-location value ("Remote - Finland", "Remote - EMEA", etc, not just the worldwide one), short enough to fit the label column without truncating mid-word',
  },
  finlandUnspecified: {
    id: 'searchPanel.finlandUnspecified',
    defaultMessage: 'Finland (unspecified)',
    description: 'Round 11: "Where they are" label for a bare "Finland" pseudo-location value (a job anchored to Finland with no city), distinguishing it from an actual Finnish city',
  },
  lensKickerMention: {
    id: 'searchPanel.lensKickerMention',
    defaultMessage: 'Mention {query}',
    description: 'Readout kicker above the count when the highlighted row is the free-text "search anywhere" row',
  },
  lensKickerListAs: {
    id: 'searchPanel.lensKickerListAs',
    defaultMessage: 'List {label} as {attributeLabel}',
    description: 'Readout kicker above the count when the highlighted row is a facet value, e.g. "List React as Skill"',
  },
});
